import { memo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Property } from '../../mocks/properties';
import MapPropertyCard from './MapPropertyCard';

interface MapSidebarProps {
  properties: Property[];
  selectedProperty: Property | null;
  onPropertySelect: (property: Property) => void;
  onPropertyView: (property: Property) => void;
  isOpen: boolean;
  onToggle: () => void;
  isLoading?: boolean;
  landmarkName?: string;
  landmarkLat?: number;
  landmarkLng?: number;
}

// Haversine distance calculation
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function MapSidebar({
  properties,
  selectedProperty,
  onPropertySelect,
  onPropertyView,
  isOpen,
  onToggle,
  isLoading,
  landmarkName,
  landmarkLat,
  landmarkLng,
}: MapSidebarProps) {
  const { t, i18n } = useTranslation(['search', 'common']);
  const direction = i18n.dir();
  const listRef = useRef<HTMLDivElement>(null);

  // Scroll to selected property
  useEffect(() => {
    if (selectedProperty && listRef.current) {
      const selectedEl = listRef.current.querySelector(`[data-property-id="${selectedProperty.id}"]`);
      if (selectedEl) {
        selectedEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [selectedProperty]);

  // Calculate distances if landmark is set
  const propertiesWithDistance = landmarkLat && landmarkLng
    ? properties.map((p) => ({
        ...p,
        distanceKm: calculateDistance(landmarkLat, landmarkLng, p.latitude, p.longitude),
      })).sort((a, b) => a.distanceKm - b.distanceKm)
    : properties.map((p) => ({ ...p, distanceKm: undefined }));

  return (
    <>
      {/* Toggle button (visible when sidebar is closed) */}
      {!isOpen && (
        <button
          type="button"
          onClick={onToggle}
          className={[
            'absolute top-1/2 -translate-y-1/2 z-40 flex items-center justify-center w-8 h-16 rounded-r-lg bg-white dark:bg-[var(--pv-color-surface-strong)] border border-gray-200 dark:border-[var(--pv-border-soft)] shadow-lg transition-transform hover:translate-x-1',
            direction === 'rtl' ? 'right-0 rounded-r-none rounded-l-lg' : 'left-0',
          ].join(' ')}
          title={t('search.map.sidebar.expand', { defaultValue: 'Show property list' })}
        >
          <i className={`ri-arrow-${direction === 'rtl' ? 'left' : 'right'}-s-line text-gray-600 dark:text-[var(--pv-color-text-muted)]`}></i>
        </button>
      )}

      {/* Sidebar */}
      <div
        className={[
          'h-full bg-white dark:bg-[var(--pv-color-surface)] border-gray-200 dark:border-[var(--pv-border-soft)] transition-all duration-300 flex flex-col',
          isOpen ? 'w-80 lg:w-96' : 'w-0',
          direction === 'rtl' ? 'border-l' : 'border-r',
        ].join(' ')}
        dir={direction}
      >
        {isOpen && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-[var(--pv-border-soft)]">
              <div>
                <h2 className="text-lg font-semibold text-[var(--pv-color-primary)] dark:text-[var(--pv-color-text)]">
                  {t('search.map.sidebar.title', { defaultValue: 'Properties' })}
                </h2>
                <p className="text-sm text-gray-500 dark:text-[var(--pv-color-text-muted)]">
                  {t('search.map.countLabel', { count: properties.length })}
                </p>
              </div>
              <button
                type="button"
                onClick={onToggle}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[var(--pv-color-surface-soft)] transition-colors"
                title={t('search.map.sidebar.collapse', { defaultValue: 'Collapse sidebar' })}
              >
                <i className={`ri-arrow-${direction === 'rtl' ? 'right' : 'left'}-s-line text-gray-600 dark:text-[var(--pv-color-text-muted)]`}></i>
              </button>
            </div>

            {/* Property list */}
            <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-3">
              {isLoading ? (
                // Loading skeleton
                Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="p-3 rounded-lg border border-gray-200 dark:border-[var(--pv-border-soft)] bg-white dark:bg-[var(--pv-color-surface-strong)] animate-pulse"
                  >
                    <div className="flex gap-3">
                      <div className="w-20 h-20 rounded-lg bg-gray-200 dark:bg-[var(--pv-color-surface-soft)]"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-200 dark:bg-[var(--pv-color-surface-soft)] rounded w-3/4"></div>
                        <div className="h-3 bg-gray-200 dark:bg-[var(--pv-color-surface-soft)] rounded w-1/2"></div>
                        <div className="h-4 bg-gray-200 dark:bg-[var(--pv-color-surface-soft)] rounded w-1/3"></div>
                      </div>
                    </div>
                    <div className="h-8 bg-gray-200 dark:bg-[var(--pv-color-surface-soft)] rounded mt-2"></div>
                  </div>
                ))
              ) : properties.length === 0 ? (
                // Empty state
                <div className="flex flex-col items-center justify-center h-full text-center py-8">
                  <i className="ri-home-line text-4xl text-gray-300 dark:text-[var(--pv-color-text-muted)] mb-3"></i>
                  <p className="text-gray-500 dark:text-[var(--pv-color-text-muted)]">
                    {t('search.results.emptyTitle')}
                  </p>
                </div>
              ) : (
                propertiesWithDistance.map((property) => (
                  <div key={property.id} data-property-id={property.id}>
                    <MapPropertyCard
                      property={property}
                      isSelected={selectedProperty?.id === property.id}
                      onSelect={onPropertySelect}
                      onView={onPropertyView}
                      distanceKm={property.distanceKm}
                      landmarkName={landmarkName}
                    />
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default memo(MapSidebar);
